import type { MsgKey } from "./i18n.ts";

/**
 * 服务端校验清单里的 `path` → 「哪一页 · 哪个字段」。
 *
 * 服务端报的是它自己的字段名（`common.costRatioBps`、`pettown.star[3].price`）。
 * 那是给写代码的人看的，运营看到只能挨页找——发布被拒的清单一次四十行，
 * 全是这种路径，等于把定位问题的工作原样丢回去。
 *
 * 这里把路径翻回运营看得懂的东西，并给出该跳去哪一页。翻不出来的照原样显示
 * （不编一个像模像样的假名字），页面为空则那一行不可点。
 */
export interface IssueTarget {
  /** 跳转目标页 id；映射不到时不给，行就不可点 */
  page?: string;
  /** 字段名的 i18n key；没有对应字段（整块问题）时不给 */
  labelKey?: MsgKey;
  /** labelKey 的占位参数（列表型问题带上是哪一行：×2 / 哪个卡包 / 第几星） */
  labelParams?: Record<string, string>;
  /** labelKey 缺失时兜底显示的原文 */
  fallback: string;
  /** 页内那一行的 id（通关奖励的 rewardId）：跳过去直接打开它的编辑弹窗 */
  rowId?: string;
}

/** cfg_main_common 的列 → 它在哪一页、叫什么。键是服务端列名的 camelCase */
const COMMON_FIELD: Record<string, { page: string; labelKey?: MsgKey }> = {
  eTargetMilliB: { page: "cost", labelKey: "p.E" },
  costRatioBps: { page: "cost", labelKey: "p.rho" },
  unifiedB: { page: "cost", labelKey: "cost.unifiedB.label" },
  incomeGrowthBps: { page: "cost", labelKey: "p.incomeK" },
  incomeAnchorB: { page: "cost", labelKey: "p.incomeAnchor" },
  targetRollsBase: { page: "cost", labelKey: "p.targetBase" },
  targetRollsGrowthBps: { page: "cost", labelKey: "p.targetGrowth" },
  diceCapBase: { page: "global-dice", labelKey: "p.cap" },
  diceCapPerLevel: { page: "global-dice", labelKey: "p.capStep" },
  diceCapPerStar: { page: "global-dice", labelKey: "p.capStarStep" },
  diceRegenSec: { page: "global-dice", labelKey: "p.regen" },
  pDoubleBps: { page: "global-dice", labelKey: "p.pDouble" },
  diceCapMax: { page: "global-dice", labelKey: "p.capMax" },
  diceInit: { page: "global-dice", labelKey: "p.diceInit" },
  subgameClientPick: { page: "global-dice", labelKey: "p.subgamePick" },
  reliefDiceQty: { page: "global-dice", labelKey: "p.reliefQty" },
  reliefDailyTimes: { page: "global-dice", labelKey: "p.reliefTimes" },
  reliefDiceThreshold: { page: "global-dice", labelKey: "p.reliefThreshold" },
  idleTapPctBps: { page: "idle-income", labelKey: "idle.pct" },
  idleTapMaxBps: { page: "idle-income", labelKey: "idle.max" },
  idleTapCapPerRoll: { page: "idle-income", labelKey: "idle.cap" },
  // 穿越概率的入口在 spin 奖励表格下面（2026-10-05）
  warpProbBps: { page: "spin", labelKey: "spin.warpProb" },

  // 这 13 个存在 cfg_main_common，但编辑入口在各自副玩法页（FIGHT_TILE_FIELDS）：
  // 「对抗与控骰」页 2026-09 起只留跨玩法共用的参数，跳过去找不到这些字段
  atkSuccessBps: { page: "subgame-thunder", labelKey: "p.atkSuccess" },
  atkBlockB: { page: "subgame-thunder", labelKey: "p.atkBlock" },
  repairPctBps: { page: "subgame-thunder", labelKey: "p.repairPct" },
  thunderPctBps: { page: "subgame-thunder", labelKey: "p.thunderPct" },
  thunderCapB: { page: "subgame-thunder", labelKey: "p.thunderCap" },
  thunderFloorB: { page: "subgame-thunder", labelKey: "p.thunderFloor" },
  stealPctBps: { page: "subgame-visit", labelKey: "p.stealPct" },
  stealCapB: { page: "subgame-visit", labelKey: "p.stealCap" },
  stealFloorB: { page: "subgame-visit", labelKey: "p.stealFloor" },
  dailyStolenCap: { page: "subgame-visit", labelKey: "p.visitCap" },
  luckyKeepDays: { page: "subgame-visit", labelKey: "p.clawbackDays" },
  luckyBonusBps: { page: "subgame-visit", labelKey: "p.clawbackBonus" },
  shieldMax: { page: "subgame-omamori", labelKey: "p.wardCap" },
  protectNpcLevel: { page: "global-fight", labelKey: "p.protectNpc" },
  protectHalfLevel: { page: "global-fight", labelKey: "p.protectHalf" },
  matchRangeLevels: { page: "global-fight", labelKey: "p.matchRange" },
  dailyGiftCap: { page: "global-fight", labelKey: "p.giftCap" },
  stakeTierBase: { page: "global-fight", labelKey: "p.stakeTierBase" },
  dailyAskCap: { page: "global-fight", labelKey: "p.askCap" },
  friendLimit: { page: "global-fight", labelKey: "p.friendLimit" },
  dailySocialEventCap: { page: "global-fight", labelKey: "p.socialEventCap" },
  dailyAvatarUploadCap: { page: "global-fight", labelKey: "p.avatarUploadCap" },
  dailyUidQueryCap: { page: "global-fight", labelKey: "p.uidQueryCap" },
  puzzleHintDailyLimit: { page: "global-fight", labelKey: "p.puzzleHintCap" },
  dailyCardLimit: { page: "global-fight", labelKey: "p.cardLimit" },
  dailyPuzzleLimit: { page: "global-fight", labelKey: "p.puzzleLimit" },
  dailyAdLimit: { page: "global-fight", labelKey: "p.adLimit" },

  // 宠物升星：五个都在升星档页（喵汪角色 → 升星档）。上限与满级发放行为都是全局的，
  // 但摆在升星档页才讲得通——运营配曲线时正要对着上限看，而不是去小镇参数页翻
  petStarMax: { page: "pet-star", labelKey: "petStar.max" },
  petMaxedCoin: { page: "pet-star", labelKey: "petStar.maxedCoin" },
  petMaxedApplyMultiplier: { page: "pet-star", labelKey: "petStar.maxedMultiplier" },
  petMaxedCountsAsAdopt: { page: "pet-star", labelKey: "petStar.maxedCountsAsAdopt" },

  // idol 亲密度：单个 idol 每日上限摆在亲密度配置页（与曲线 / 来源同页）
  intimacyIdolDailyCap: { page: "idol-intimacy", labelKey: "intimacy.idolDailyCap" },

  pettownMaxStar: { page: "pettown-params", labelKey: "pt.maxStar" },
  pettownStarsPerLevel: { page: "pettown-params", labelKey: "pt.starsPerLevel" },
  pettownRepairPctBps: { page: "pettown-params", labelKey: "pt.repairPct" },
  pettownAttackerPctBps: { page: "pettown-params", labelKey: "pt.attackerPct" },
  pettownProtectStar: { page: "pettown-params", labelKey: "pt.protectStar" },
  lampPityThreshold: { page: "lamp-drops", labelKey: "lamp.pity.threshold" },
  pettownMatchRangeStars: { page: "pettown-params", labelKey: "pt.matchRangeStars" },

  // 这两项没有独立的字段名 key，抬头就是页名，fallback 会显示原路径
  buildingSystem: { page: "init-config" },
  buildingSet: { page: "init-config" },
};

/** 路径前缀 → 页面。按最长前缀匹配，写在前面的先命中 */
const PREFIX_PAGE: Array<[RegExp, string]> = [
  [/^gameMode/, "init-config"],
  // 多人三张表各有自己的页了（2026-09-09 前 matchMode 只能送去初始化页）
  [/^matchMode/, "match-mode"],
  [/^battleStage/, "battle-stage"],
  [/^abilityCard/, "ability-card"],
  // 星是唯一的配置单位（2026-09-17）：星级表的规则报 pettown.star[N]，总星数报 common.pettownMaxStar
  [/^pettown\.stars?\b/, "pettown-star"],
  [/^pettown\.slot/, "pettown-slot"],
  [/^(?:levels?|city)\[\d+\]\.(?:targetRolls(?:Override)?|target_rolls(?:_override)?|gMilli|g_milli)\b/, "cost"],
  [/^(?:levels?|city)\[\d+\]\.(?:n_buildings|n_levels|nBuildings|nLevels|N|M|cells)\b/, "building"],
  [/^(?:levels?|city)\[\d+\]\.(?:rollsRange|rolls_range)\b/, "sim"],
  [/^levels?\b/, "level"],
  [/^city\[/, "level"],
  [/^buildingSet\b/, "init-config"],
  [/^spin\b/, "spin"],
  [/^drops?\b/, "reward"],
  [/^reward\b/, "reward"],
  [/^pack\b/, "card-item"],
  [/^auction\b/, "auction"],
  [/^puzzlePrice/, "puzzle-price"],
  [/^puzzle\b/, "puzzle-list"],
  [/^daily\b/, "daily-reward"],
  [/^channel\b/, "social-channel"],
  [/^eventType\b/, "social-event-type"],
  [/^frames\b/, "frame"],
  [/^boardCells\b/, "board-cells"],
  // 服务端报 intimacyLevels[3].expTotal / intimacySources[greet].points（见 game/intimacy-rules.ts）
  [/^intimacy(Levels|Sources)\b/, "idol-intimacy"],
  /*
   * ⚠️ 必须写 `pets?`。服务端报的是 `pets[C001].quality`（复数带下标，
   * 见 config-validate.ts），而 `/^pet\b/` **匹配不到它**——`\b` 要求词与非词的
   * 边界，`t` 与 `s` 之间不成立。于是宠物那几条从来都是不可点的，
   * 而界面上看不出区别：不可点和「没有这一条」长得一样。
   */
  [/^pets?\b/, "pet-list"],
  // 升星档报 petStars / petStars[3].dupCost（要排在 pets? 之后也没关系：`petStars` 里 t 与 S 之间没有词边界，那条不命中）
  [/^petStars\b/, "pet-star"],
  // 天赋树报 talent；萌宠小镇与关卡制各有一页，App 跳转时按建筑体系换成 level-talent
  [/^talent\b/, "pettown-talent"],
  [/^achievement\b/, "achievement"],
];

/** 玩法格：`tile.jackpot` → 那个格子自己的页 */
const TILE_RE = /^tile\.([a-z0-9_]+)/;

/**
 * 领养池：`adoptPools[shop].members` → 收养馆那一格的页。
 *
 * 2026-09-11 起领养池按点拆行、配置并入副玩法格页，所以落点**只能按点分**。
 * 服务端每条领养池规则都带下标（`ADOPT_POOL_REQUIRED` 也改成了 `adoptPools[<点>]`）。
 *
 * ⚠️ 万一还是来了个**裸** `adoptPools`（旧版本的服务端、或将来新加的规则忘了带点），
 * **不给页面**，让那一行不可点。不要兜底送去收养馆：一个点一页，
 * 缺草丛却跳收养馆会把人送到一页好着的配置前面——而那一行看起来是可点的，
 * 比不可点更误导。不可点至少和 fallback 显示的原始路径一起，说明「这里定位不了」。
 */
const ADOPT_RE = /^adoptPools?\[([a-z_]+)\]/;

/*
 * 服务端报**复数**的几块（`cardPacks[x]`、`dailyRewards[..]`、`auctions[..]`、
 * `puzzles.byIdol[..]`、`socialEventTypes[..]`、`tiles[x]`），上面那张表按单数写，一条都命中不了，
 * 那几行只剩一串英文路径、不可点（2026-09-14 a0=12 v63 发布被拒 61 行，运营找不到去哪改）。
 * 放在 PREFIX_PAGE 之前单独匹配。
 */
const PLURAL_PAGE: Array<[RegExp, string]> = [
  // 倍率档在「骰子参数」页顶上那块（MultiplierSettings）
  // idol 卡包与物品卡包同一张表，路径分不出来；只有 idol_id 这一条确定是 idol 卡包
  [/^cardPacks\[[^\]]+\]\.idol_id\b/, "card-idol"],
  // 按玩家等级的掉落（CARD_LEVEL_TOTAL / RANGE / RATE / NEEDS_CITIES）只在「物品掉落配置」页编辑
  [/^cardPacks\.level\b/, "card-drop"],
  [/^cardPacks\[[^\]]+\]\.drops\b/, "card-drop"],
  [/^cardPacks$/, "card-drop"],
  [/^cardPacks\b/, "card-item"],
  [/^dailyRewards\b/, "daily-reward"],
  // 通关奖励报 `rewards[<rewardId>].items[1].ref`；PREFIX_PAGE 的 `reward\b` 在 d 与 s 之间没有词边界，
  // 这几行曾全落进「其他」、不可点（2026-10-21 a0=1201 v130）
  [/^rewards\b/, "reward"],
  [/^auctions\b/, "auction"],
  [/^puzzles\b/, "puzzle-list"],
  [/^socialEventTypes\b/, "social-event-type"],
];

/** 列表型问题的字段名：带上是哪一行，灰色小字里还有原始路径 */
const ROW_LABEL: Array<[RegExp, MsgKey, (m: RegExpExecArray) => Record<string, string>]> = [
  // 第几项按 1 起数，与弹窗里从上往下数对得上；哪一关由 App 按 rowId 查（这里只有 rewardId）
  [/^rewards\[[^\]]+\]\.items\[(\d+)\]/, "issue.rewardItem", (m) => ({ n: String(Number(m[1]) + 1) })],
  [/^rewards\[[^\]]+\]\.name_i18n/, "issue.rewardName", () => ({})],
  [/^cardPacks\[([^\]]+)\]\.starDrops/, "issue.packStarDrops", (m) => ({ id: m[1] })],
  [/^cardPacks\.star\[(\d+)\]/, "issue.packStarTotal", (m) => ({ star: m[1] })],
];

function rowLabel(path: string): Pick<IssueTarget, "labelKey" | "labelParams"> {
  for (const [re, labelKey, params] of ROW_LABEL) {
    const m = re.exec(path);
    if (m) return { labelKey, labelParams: params(m) };
  }
  return {};
}

const TILES_RE = /^tiles\[([a-z0-9_]+)\]/;

/**
 * idol 关卡相册 / VIP 照片 / VIP 视频：服务端报 `idol_photos[ph_0003].img`、`idol_vip_videos[vv_0001].duration_sec`
 * （见 src/admin/config-validate.ts 的 validateIdolAlbum，规则码 IDOL_PHOTO_* / IDOL_VIP_PHOTO_* / IDOL_VIP_VIDEO_*）。
 * 落点按块分页；字段名带上是哪一行（ID），运营到页上按 ID 找那一行
 */
const ALBUM_RE = /^(idol_photos|idol_vip_photos|idol_vip_videos|stages|stage_images)\[([^\]]+)\](?:\.([a-z_]+))?/;
const ALBUM_PAGE: Record<string, string> = {
  idol_photos: "idol-photos",
  idol_vip_photos: "idol-vip-photos",
  idol_vip_videos: "idol-vip-videos",
  // 关卡多图（改版）：关卡与关卡图片都在「关卡图片」页；跨行的两条（张数上限 / 缺关卡图）报在 stages[st_x].images
  stages: "stage-images",
  stage_images: "stage-images",
};
const ALBUM_FIELD: Record<string, MsgKey> = {
  puzzle_id: "issue.album.puzzle",
  slot: "issue.album.slot",
  thumb_img: "issue.album.thumb",
  img: "issue.album.img",
  unlock_rule: "issue.album.rule",
  lock_rule: "issue.album.rule",
  idol_id: "issue.album.idol",
  cover_img: "issue.album.cover",
  duration_sec: "issue.album.duration",
  video_url: "issue.album.video",
  level_code: "issue.stage.code",
  reward: "issue.stage.reward",
  image_type: "issue.stage.type",
  grid_n: "issue.stage.grid",
  price_frag: "issue.stage.price",
  images: "issue.stage.images",
  unlock_cost: "stage.col.unlock",
  unlock_vip: "stage.unlockVip",
};

function albumTarget(path: string): IssueTarget | null {
  const m = ALBUM_RE.exec(path);
  if (!m) return null;
  const labelKey = m[3] ? ALBUM_FIELD[m[3]] : undefined;
  return labelKey
    ? { page: ALBUM_PAGE[m[1]], labelKey, labelParams: { id: m[2] }, fallback: path }
    : { page: ALBUM_PAGE[m[1]], fallback: path };
}

export function issueTarget(path: string): IssueTarget {
  if (/^levels\[\d+\]\.costMatrixSeed$/.test(path)) return { page: "building", labelKey: "building.matrixSeed", fallback: path };
  const album = albumTarget(path);
  if (album) return album;
  const tiles = TILES_RE.exec(path);
  // 活动道具格的配置在「活动道具」页顶上（不单独出菜单）
  if (tiles) return { page: tiles[1] === "act_item" ? "act-item" : `subgame-${tiles[1]}`, fallback: path };
  const reward = /^rewards\[([^\]]+)\]/.exec(path);
  for (const [re, page] of PLURAL_PAGE) if (re.test(path)) return { page, ...rowLabel(path), fallback: path, ...(reward ? { rowId: reward[1] } : {}) };
  const common = /^common\.([A-Za-z0-9_]+)/.exec(path);
  if (common) {
    const hit = COMMON_FIELD[common[1]];
    // 没登记的列多半是页面上没有编辑入口的（历史列、透传列）：给不出页面就不给，
    // 别把人送到一个找不到这个字段的页面上
    return hit ? { page: hit.page, labelKey: hit.labelKey, fallback: path } : { fallback: path };
  }
  const tile = TILE_RE.exec(path);
  if (tile) return { page: `subgame-${tile[1]}`, fallback: path };
  // 领养池要在 PREFIX_PAGE 之前：它得从路径里取出领养点才知道送去哪一格
  const adopt = ADOPT_RE.exec(path);
  if (adopt) return { page: `subgame-adopt_${adopt[1]}`, fallback: path };
  for (const [re, page] of PREFIX_PAGE) if (re.test(path)) return { page, fallback: path };
  return { fallback: path };
}
